/**
 * UserProfile - Fiche utilisateur / Profil équipe
 * Route: /dashboard/team/user/:userId
 *
 * Vue 360° d'un membre de l'équipe (Admin + VP, RBAC backend).
 * Architecture unifiée : reprend la logique du Dashboard apporteur (KPIs cliquables
 * -> liste des dossiers), enrichie d'infos personnelles, tâches, événements et notes
 * issues des dossiers de l'utilisateur.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { loadUserProfile, userProfileKey, prefetchProspect } from '@/lib/resources';
import { getCached } from '@/lib/dataCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { BackButton, OverviewButton } from '@/components/common/NavButtons';
import { UserImplicationCard } from '@/components/shared/UserImplicationCard';
import { ParrainMonthlyChart } from '@/components/parrainage/ParrainMonthlyChart';
import { cn } from '@/lib/utils';
import {
    Briefcase, Clock, CheckCircle2, Euro, XCircle, Activity,
    Mail, Phone, Crown, Trophy, Target, Users, Calendar, ListTodo,
    MapPin, MessageSquare, ArrowRight, TrendingUp,
} from 'lucide-react';

const formatCurrency = (value) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);

const initials = (first, last) => `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';

const ROLE_LABELS = {
    admin_full: 'Admin Full',
    admin: 'Admin',
    vp: 'VP',
    apporteur: 'Apporteur',
    comptable: 'Comptable',
};

const VALIDATED_EP_STAGES = ['ep_gagne', 'ep_contrat_signe', 'ep_questionnaire_chantier', 'ep_commission_percue'];
const VALIDATED_CH_STAGES = ['ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue', 'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours', 'ch_cloture', 'ch_valide', 'ch_paiement_effectue', 'ch_termine', 'ch_chantier_termine', 'ch_attente_paiement', 'ch_gagne'];

const isWon = (p) => VALIDATED_EP_STAGES.includes(p.easy_power_stage) || VALIDATED_CH_STAGES.includes(p.chantier_stage);
const isLost = (p) => p.easy_power_stage === 'ep_perdu' || p.chantier_stage === 'ch_perdu';

const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
};
const fmtDateTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

export default function UserProfile() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profile, setProfile] = useState(null);
    const [prospects, setProspects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [events, setEvents] = useState([]);
    const [notes, setNotes] = useState([]);
    // Onglet KPI ouvert = persisté dans l'URL (préserve le contexte au retour).
    const selectedKpi = searchParams.get('kpi') || null;

    const loadData = useCallback(async () => {
        const key = userProfileKey(userId);
        const cached = getCached(key);
        if (cached) {
            // Affichage instantané des données déjà consultées
            setProfile(cached.profile);
            setProspects(cached.prospects);
            setTasks(cached.tasks);
            setEvents(cached.events);
            setNotes(cached.notes);
            setError(null);
            setLoading(false);
        } else {
            setLoading(true);
        }
        try {
            const data = await loadUserProfile(userId);
            setProfile(data.profile);
            setProspects(data.prospects);
            setTasks(data.tasks);
            setEvents(data.events);
            setNotes(data.notes);
            setError(null);
        } catch (e) {
            if (!cached) {
                setError(e?.response?.status === 403 ? "Vous n'avez pas accès à cette fiche." : "Impossible de charger la fiche utilisateur.");
            }
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { loadData(); }, [loadData]);

    const stats = profile?.statistics || {};
    const vpComm = profile?.vp_commission;
    const wonList = prospects.filter(isWon);
    const lostList = prospects.filter(isLost);
    const inProgressList = prospects.filter((p) => !isWon(p) && !isLost(p) && (p.easy_power_stage || p.chantier_stage));

    const dossierCommission = (p) => {
        if (p.dossier_vp_id === userId || p.dossier_vp_2_id === userId) {
            return (p.ep_commission_vp_payeur_nette || 0) + (p.ep_commission_vp_superviseur || 0)
                + (p.ch_commission_vp_payeur_nette || 0) + (p.ch_commission_vp_superviseur || 0);
        }
        return (p.ep_commission_apporteur_1 || p.ep_commission_apporteur || 0) + (p.ep_commission_apporteur_2 || 0)
            + (p.ch_commission_apporteur_1 || p.ch_commission_apporteur || 0) + (p.ch_commission_apporteur_2 || 0);
    };

    const kpiConfig = {
        total: { label: 'Dossiers', items: prospects, showCommission: false },
        won: { label: 'Gagnés', items: wonList, showCommission: true },
        in_progress: { label: 'En cours', items: inProgressList, showCommission: false },
        lost: { label: 'Perdus', items: lostList, showCommission: false },
        commission: { label: 'Commission', items: wonList, showCommission: true },
    };
    const toggleKpi = (k) => {
        const next = new URLSearchParams(searchParams);
        if (selectedKpi === k) next.delete('kpi'); else next.set('kpi', k);
        setSearchParams(next, { replace: true });
    };
    const closeKpi = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('kpi');
        setSearchParams(next, { replace: true });
    };
    const selected = selectedKpi ? kpiConfig[selectedKpi] : null;

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6" data-testid="user-profile-loading">
                    <Skeleton className="h-10 w-40" />
                    <Skeleton className="h-32 w-full" />
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-64 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="max-w-lg mx-auto mt-12 text-center space-y-4" data-testid="user-profile-error">
                    <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                    <p className="text-slate-600">{error}</p>
                    <div className="flex items-center justify-center gap-2">
                        <BackButton testid="user-profile-back-error" />
                        <OverviewButton testid="user-profile-overview-error" />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    const rankings = profile.rankings || {};

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="user-profile-page">
                {/* Navigation : Retour (historique) + Vue d'ensemble (Dashboard) */}
                <div className="flex items-center gap-2">
                    <BackButton testid="user-profile-back" className="-ml-2" />
                    <OverviewButton testid="user-profile-overview" />
                </div>

                {/* En-tête : infos personnelles */}
                <Card className="border-slate-200" data-testid="user-profile-header">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <Avatar className="h-16 w-16 border-2 border-purple-100">
                                {profile.profile_picture && <AvatarImage src={profile.profile_picture} alt={fullName} />}
                                <AvatarFallback className="bg-purple-100 text-purple-700 text-xl font-semibold">
                                    {initials(profile.first_name, profile.last_name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h1 className="text-2xl font-outfit font-semibold text-slate-900 truncate" data-testid="user-profile-name">{fullName}</h1>
                                    <Badge className="bg-purple-100 text-purple-700 hover:opacity-90" data-testid="user-profile-role">
                                        {profile.role === 'vp' && <Crown className="w-3 h-3 mr-1" />}
                                        {ROLE_LABELS[profile.role] || profile.role}
                                    </Badge>
                                    {profile.is_active === false && <Badge variant="outline" className="text-red-500 border-red-200">Inactif</Badge>}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                                    {profile.email && (
                                        <a href={`mailto:${profile.email}`} className="flex items-center gap-1.5 hover:text-purple-700" data-testid="user-profile-email">
                                            <Mail className="w-4 h-4" /> {profile.email}
                                        </a>
                                    )}
                                    {profile.phone && (
                                        <a href={`tel:${profile.phone}`} className="flex items-center gap-1.5 hover:text-purple-700" data-testid="user-profile-phone">
                                            <Phone className="w-4 h-4" /> {profile.phone}
                                        </a>
                                    )}
                                    {profile.vp_referent && (
                                        <span className="flex items-center gap-1.5">
                                            <Crown className="w-4 h-4 text-amber-500" /> VP : {profile.vp_referent.name}
                                        </span>
                                    )}
                                </div>
                                {(profile.teams || []).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="user-profile-teams">
                                        {profile.teams.map((t) => (
                                            <Badge key={t.id} variant="outline" className="text-slate-600 gap-1">
                                                <Users className="w-3 h-3" /> {t.name} · {t.role}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Classements */}
                            <div className="flex gap-3 shrink-0">
                                {rankings.global_rank > 0 && (
                                    <div className="text-center px-4 py-2 rounded-xl bg-amber-50 border border-amber-100" data-testid="user-profile-rank-global">
                                        <Trophy className="w-5 h-5 text-amber-500 mx-auto" />
                                        <p className="text-lg font-bold text-amber-700 mt-1">#{rankings.global_rank}</p>
                                        <p className="text-[10px] text-amber-600 uppercase">Global / {rankings.total_users}</p>
                                    </div>
                                )}
                                {rankings.team_rank > 0 && (
                                    <div className="text-center px-4 py-2 rounded-xl bg-purple-50 border border-purple-100" data-testid="user-profile-rank-team">
                                        <Users className="w-5 h-5 text-purple-500 mx-auto" />
                                        <p className="text-lg font-bold text-purple-700 mt-1">#{rankings.team_rank}</p>
                                        <p className="text-[10px] text-purple-600 uppercase">Équipe / {rankings.team_size}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Synthèse d'activité — KPIs cliquables */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="user-profile-kpis">
                    <KpiCard testid="kpi-total" label="Dossiers" value={stats.total_prospects ?? prospects.length}
                        icon={Briefcase} color="blue" active={selectedKpi === 'total'} onClick={() => navigate(`/prospects?user=${userId}`)} />
                    <KpiCard testid="kpi-won" label="Gagnés" value={stats.won_prospects ?? wonList.length}
                        icon={CheckCircle2} color="green" active={selectedKpi === 'won'} onClick={() => navigate(`/prospects?user=${userId}&view=gagnes`)} />
                    <KpiCard testid="kpi-in-progress" label="En cours" value={stats.in_progress ?? inProgressList.length}
                        icon={Clock} color="amber" active={selectedKpi === 'in_progress'} onClick={() => navigate(`/prospects?user=${userId}&view=en_cours`)} />
                    <KpiCard testid="kpi-lost" label="Perdus" value={stats.lost_prospects ?? lostList.length}
                        icon={XCircle} color="red" active={selectedKpi === 'lost'} onClick={() => navigate(`/prospects?user=${userId}&pipeline=lost`)} />
                    <KpiCard testid="kpi-commission" label="Commission" value={formatCurrency(stats.total_commission)}
                        icon={Euro} color="emerald" active={selectedKpi === 'commission'} onClick={() => navigate(`/prospects?user=${userId}&view=gagnes`)} isMoney />
                </div>

                {/* Fiche Parrain — chaîne parrainage (VP, parent, downlines) + overrides + compteur dossiers */}
                {profile.parrain_info && (
                    <Card className="border-indigo-100" data-testid="user-profile-parrain-info">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="w-5 h-5 text-indigo-600" /> Réseau Parrainage · Niveau {profile.parrain_info.level ?? '—'}
                                {profile.parrain_info.branch && (
                                    <Badge variant="outline" className="text-xs font-normal border-indigo-200 text-indigo-700">Branche {profile.parrain_info.branch}</Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Position dans la chaîne */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {profile.parrain_info.vp && (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-[11px] font-semibold uppercase text-slate-500 flex items-center gap-1"><Crown className="w-3 h-3 text-amber-500" /> VP de rattachement</p>
                                        <p className="text-sm font-medium text-slate-800 mt-0.5">{profile.parrain_info.vp.name}</p>
                                    </div>
                                )}
                                {profile.parrain_info.parent ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/dashboard/team/user/${profile.parrain_info.parent.id}`)}
                                        className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-left hover:border-indigo-400 hover:bg-indigo-100 transition"
                                        data-testid="parrain-info-parent"
                                    >
                                        <p className="text-[11px] font-semibold uppercase text-indigo-600">Parrain de rattachement (N{profile.parrain_info.parent.level ?? '?'})</p>
                                        <p className="text-sm font-medium text-indigo-900 mt-0.5">{profile.parrain_info.parent.name}</p>
                                    </button>
                                ) : (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-[11px] font-semibold uppercase text-slate-500">Parrain de rattachement</p>
                                        <p className="text-sm text-slate-500 italic mt-0.5">Aucun (Niveau 1)</p>
                                    </div>
                                )}
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                    <p className="text-[11px] font-semibold uppercase text-emerald-700">Dossiers parrainage</p>
                                    <p className="text-lg font-bold text-emerald-800 mt-0.5" data-testid="parrain-info-dossiers-count">{profile.parrain_info.dossiers_count ?? 0}</p>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/prospects?parrain=${userId}&origin=parrainage`)}
                                        className="mt-1 text-[11px] font-semibold text-emerald-700 hover:underline"
                                        data-testid="parrain-info-dossiers-link"
                                    >
                                        Voir tous les dossiers →
                                    </button>
                                </div>
                            </div>

                            {/* Downlines : N+1 et N+2 directement rattachés à ce parrain */}
                            {(profile.parrain_info.downlines || []).length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Parrains rattachés en dessous ({profile.parrain_info.downlines.length})</p>
                                    <div className="space-y-1.5">
                                        {profile.parrain_info.downlines.map((d) => (
                                            <button
                                                key={d.id}
                                                type="button"
                                                onClick={() => navigate(`/dashboard/team/user/${d.id}`)}
                                                className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 px-3 py-2 transition text-left"
                                                data-testid={`parrain-downline-${d.id}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-semibold text-indigo-500 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">N{d.level}</span>
                                                    <span className="text-sm font-medium text-slate-800">{d.name}</span>
                                                </div>
                                                <span className="text-xs text-slate-400">→ Fiche</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Override individuel (N1 uniquement) */}
                            {profile.parrain_info.level === 1 && (profile.parrain_info.override_ep != null || profile.parrain_info.override_ch != null) && (
                                <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                                    <p className="text-[11px] font-semibold uppercase text-purple-700 mb-1">Taux personnalisé N1 (budget total sur la branche)</p>
                                    <div className="flex gap-4 text-sm">
                                        {profile.parrain_info.override_ep != null && (
                                            <span><span className="text-slate-500">Courtage :</span> <strong className="text-purple-900">{profile.parrain_info.override_ep}%</strong></span>
                                        )}
                                        {profile.parrain_info.override_ch != null && (
                                            <span><span className="text-slate-500">Travaux d’optimisation :</span> <strong className="text-purple-900">{profile.parrain_info.override_ch}%</strong></span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Graphe mensuel des commissions parrainage (parrains uniquement) */}
                {profile.parrain_info && (
                    <ParrainMonthlyChart userId={userId} />
                )}

                {/* Taux de réussite */}
                <Card data-testid="user-profile-success-rate">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-full bg-blue-50"><Target className="w-5 h-5 text-blue-600" /></div>
                            <div>
                                <p className="text-sm text-slate-500">Taux de réussite</p>
                                <p className="text-2xl font-bold text-blue-700">{stats.success_rate ?? 0}%</p>
                            </div>
                            {stats.avg_processing_days > 0 && (
                                <div className="ml-auto text-right">
                                    <p className="text-sm text-slate-500 flex items-center gap-1 justify-end"><TrendingUp className="w-4 h-4" /> Délai moyen</p>
                                    <p className="text-lg font-semibold text-slate-700">{stats.avg_processing_days} j</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Commission VP par équipe (fiche VP) — direct / apporteurs / par équipe */}
                {vpComm && (
                    <Card data-testid="user-profile-vp-commission">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Crown className="w-5 h-5 text-indigo-600" /> Commissions VP {vpComm.multi_team ? '(toutes équipes)' : ''}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Totaux (toutes équipes confondues) */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vp-commission-totals">
                                <VpCommStat label="Gagné en direct" hint="Vos dossiers signés seul" value={vpComm.totals.direct} color="indigo"
                                    onClick={() => navigate(`/prospects?user=${userId}&view=gagnes`)} />
                                <VpCommStat label="Gagné via apporteurs" hint="Votre part sur les dossiers de l'équipe" value={vpComm.totals.indirect} color="violet"
                                    onClick={() => navigate(`/prospects?user=${userId}&view=gagnes`)} />
                                <VpCommStat label="Commissions apporteurs" hint="Ce que touchent vos apporteurs" value={vpComm.totals.apporteurs_commission} color="emerald"
                                    onClick={vpComm.teams.length === 1 ? () => navigate(`/prospects?team=${vpComm.teams[0].team_id}&view=gagnes`) : undefined} />
                                {vpComm.totals.apporteurs_volume != null && (
                                    <VpCommStat label="Chiffre d'affaire total généré" hint="CA total des dossiers, partagé Valnexo / AT Service" value={vpComm.totals.apporteurs_volume} color="slate"
                                        onClick={vpComm.teams.length === 1 ? () => navigate(`/prospects?team=${vpComm.teams[0].team_id}&view=gagnes`) : undefined} />
                                )}
                            </div>
                            {/* Détail par équipe si le VP est sur plusieurs équipes */}
                            {vpComm.multi_team && (
                                <div className="space-y-2" data-testid="vp-commission-teams">
                                    <p className="text-[11px] font-semibold text-slate-400 uppercase">Détail par équipe</p>
                                    {vpComm.teams.map((t) => (
                                        <div key={t.team_id}
                                            onClick={() => navigate(`/prospects?team=${t.team_id}&view=gagnes`)}
                                            className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/40 transition"
                                            data-testid={`vp-commission-team-${t.team_id}`}>
                                            <p className="text-sm font-semibold text-slate-800 mb-1.5">{t.team_name}</p>
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-[12px]">
                                                <span className="text-slate-500">Direct <b className="text-indigo-700">{formatCurrency(t.direct)}</b></span>
                                                <span className="text-slate-500">Via apporteurs <b className="text-violet-700">{formatCurrency(t.indirect)}</b></span>
                                                <span className="text-slate-500">Com. apporteurs <b className="text-emerald-700">{formatCurrency(t.apporteurs_commission)}</b></span>
                                                {t.apporteurs_volume != null && (
                                                    <span className="text-slate-500">CA total <b className="text-slate-700">{formatCurrency(t.apporteurs_volume)}</b></span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Fiche d'implication (sous le taux de réussite) — visibilité gérée côté backend */}
                <UserImplicationCard userId={userId} />

                {/* Liste des dossiers du KPI sélectionné */}
                {selected && (
                    <Card data-testid="user-profile-kpi-list">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-slate-600" />
                                    {selected.label} — {selected.items.length} dossier{selected.items.length > 1 ? 's' : ''}
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={closeKpi} data-testid="user-profile-kpi-list-close">Fermer</Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {selected.items.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-6">Aucun dossier dans cette catégorie.</p>
                            ) : (
                                <div className="divide-y">
                                    {selected.items.map((p) => (
                                        <div key={p.id} onClick={() => navigate(`/prospects/${p.id}`)}
                                            onMouseEnter={() => prefetchProspect(p.id)}
                                            className="flex items-center justify-between gap-3 py-3 cursor-pointer hover:bg-slate-50 -mx-4 px-4"
                                            data-testid={`user-profile-dossier-${p.id}`}>
                                            <div className="min-w-0">
                                                <p className="font-medium truncate">{p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}</p>
                                                <p className="text-sm text-slate-500 truncate">{p.city || p.first_name || ''}</p>
                                            </div>
                                            {selected.showCommission ? (
                                                <span className="text-sm font-bold text-emerald-700 shrink-0">{formatCurrency(dossierCommission(p))}</span>
                                            ) : (
                                                <Badge variant="outline" className="shrink-0">{p.easy_power_stage || p.chantier_stage || 'En cours'}</Badge>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Tâches & Événements */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tâches */}
                    <Card data-testid="user-profile-tasks">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <ListTodo className="w-5 h-5 text-slate-600" /> Tâches ({tasks.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {tasks.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-6">Aucune tâche.</p>
                            ) : (
                                <div className="space-y-2">
                                    {tasks.slice(0, 8).map((t) => (
                                        <div key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2"
                                            data-testid={`user-profile-task-${t.id}`}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-700 truncate">{t.title}</p>
                                                {t.prospect_name && <p className="text-xs text-slate-400 truncate">{t.prospect_name}</p>}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {t.due_date && <span className="text-xs text-slate-400">{fmtDate(t.due_date)}</span>}
                                                <Badge variant="outline" className={cn('text-xs',
                                                    t.status === 'completed' && 'text-green-600 border-green-200',
                                                    t.status === 'in_progress' && 'text-amber-600 border-amber-200')}>
                                                    {t.status === 'completed' ? 'Terminée' : t.status === 'in_progress' ? 'En cours' : 'À faire'}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Événements */}
                    <Card data-testid="user-profile-events">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-slate-600" /> Événements ({events.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {events.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-6">Aucun événement.</p>
                            ) : (
                                <div className="space-y-2">
                                    {events.slice(0, 8).map((ev) => (
                                        <div key={ev.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2"
                                            data-testid={`user-profile-event-${ev.id}`}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-700 truncate">{ev.title}</p>
                                                {ev.location && <p className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location}</p>}
                                            </div>
                                            <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(ev.start_datetime)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Notes issues des dossiers */}
                <Card data-testid="user-profile-notes">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-slate-600" /> Notes récentes des dossiers
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {notes.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">Aucune note sur les dossiers de cet utilisateur.</p>
                        ) : (
                            <div className="space-y-3">
                                {notes.map((n) => (
                                    <div key={n.id} className="rounded-lg border border-slate-100 p-3" data-testid={`user-profile-note-${n.id}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <Avatar className="h-6 w-6">
                                                {n.author_avatar && <AvatarImage src={n.author_avatar} />}
                                                <AvatarFallback className="bg-slate-100 text-slate-600 text-[10px]">{(n.author_name || '?')[0]}</AvatarFallback>
                                            </Avatar>
                                            <span className="text-sm font-medium text-slate-700">{n.author_name}</span>
                                            <button onClick={() => navigate(`/prospects/${n.prospect_id}`)}
                                                className="text-xs text-purple-600 hover:underline ml-1 flex items-center gap-0.5">
                                                {n.prospect_label} <ArrowRight className="w-3 h-3" />
                                            </button>
                                            <span className="text-xs text-slate-400 ml-auto">{fmtDateTime(n.created_at)}</span>
                                        </div>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{n.content}</p>
                                        {(n.mentioned_names || []).length > 0 && (
                                            <p className="text-xs text-slate-400 mt-1">Mentionné(s) : {n.mentioned_names.join(', ')}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}

const COLOR_MAP = {
    blue: { text: 'text-blue-600', ring: 'ring-blue-400', icon: 'text-blue-500' },
    green: { text: 'text-green-700', ring: 'ring-green-400', icon: 'text-green-500' },
    amber: { text: 'text-amber-600', ring: 'ring-amber-400', icon: 'text-amber-500' },
    red: { text: 'text-red-600', ring: 'ring-red-400', icon: 'text-red-500' },
    emerald: { text: 'text-emerald-700', ring: 'ring-emerald-400', icon: 'text-emerald-500' },
};

function KpiCard({ testid, label, value, icon: Icon, color, active, onClick, isMoney }) {
    const c = COLOR_MAP[color] || COLOR_MAP.blue;
    return (
        <Card role="button" tabIndex={0} data-testid={testid} onClick={onClick}
            className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5', active && `ring-2 ${c.ring}`)}>
            <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                    <div className="min-w-0">
                        <p className="text-xs text-slate-500">{label}</p>
                        <p className={cn('font-bold', c.text, isMoney ? 'text-xl' : 'text-2xl')}>{value}</p>
                    </div>
                    <Icon className={cn('w-8 h-8 opacity-50 shrink-0', c.icon)} />
                </div>
            </CardContent>
        </Card>
    );
}


const VP_COMM_COLORS = {
    indigo: 'text-indigo-700 border-indigo-200 bg-indigo-50/60',
    violet: 'text-violet-700 border-violet-200 bg-violet-50/60',
    emerald: 'text-emerald-700 border-emerald-200 bg-emerald-50/60',
    slate: 'text-slate-700 border-slate-200 bg-slate-50/60',
};
function VpCommStat({ label, hint, value, color, onClick }) {
    const c = VP_COMM_COLORS[color] || VP_COMM_COLORS.slate;
    const clickable = typeof onClick === 'function';
    return (
        <div onClick={onClick} className={cn('rounded-xl border px-3 py-2.5', c, clickable && 'cursor-pointer hover:shadow-sm hover:brightness-95 transition')} data-testid={`vp-comm-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold leading-tight flex items-center gap-1">{label}{clickable && <span className="opacity-60">›</span>}</p>
            <p className={cn('text-lg font-bold mt-0.5', c.split(' ')[0])}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0)}
            </p>
            {hint && <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{hint}</p>}
        </div>
    );
}
